import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { chatApi, S3_BASE_URL } from './api';
import { openPrimeModal } from './PrimeModal';

const IconChat = ({ size = 24, color = 'white' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
    </svg>
);
const IconClose = ({ size = 20, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
);
const IconArrowLeft = ({ size = 20, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="19" y1="12" x2="5" y2="12"></line>
        <polyline points="12 19 5 12 12 5"></polyline>
    </svg>
);
const IconSend = ({ size = 20, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="22" y1="2" x2="11" y2="13"></line>
        <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
    </svg>
);

const Avatar = ({ src, name, size = 48 }) => {
    const [imgError, setImgError] = useState(false);
    const initial = name?.[0]?.toUpperCase() || '?';
    const url = src ? (src.startsWith('http') ? src : `${S3_BASE_URL}${src.startsWith('/') ? src.substring(1) : src}`) : null;

    if (!url || imgError) {
        return (
            <div style={{ width: size, height: size, borderRadius: '50%', background: 'linear-gradient(135deg, #1E40AF, #3B82F6)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '800', fontSize: size * 0.4, flexShrink: 0 }}>
                {initial}
            </div>
        );
    }
    return <img src={url} alt={name} onError={() => setImgError(true)} style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', background: '#DDE3DC', flexShrink: 0 }} />;
};

const BLUE = '#2563EB';
const BLUE_DARK = '#1E40AF';

const ChatWidget = () => {
    const { isAuthenticated, tokens, user } = useAuth();
    const [isOpen, setIsOpen] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [rooms, setRooms] = useState([]);
    const [roomsLoaded, setRoomsLoaded] = useState(false);

    // activeRoom: { id, otherUserId, otherUserName, otherUserProfilePic }
    const [activeRoom, setActiveRoom] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loadingRoom, setLoadingRoom] = useState(false);
    const [sendingMessage, setSendingMessage] = useState(false);

    const messagesEndRef = useRef(null);
    const pollingRef = useRef(null);
    const inputRef = useRef(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 480);

    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth < 480);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // ── Helpers ──────────────────────────────────────────────────────
    const scrollToBottom = useCallback(() => {
        // Two-frame delay so React has painted the new messages
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            });
        });
    }, []);

    const stopPolling = useCallback(() => {
        if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
    }, []);

    const loadMessages = useCallback(async (room) => {
        if (!room?.id || !tokens?.access) return;
        try {
            const data = await chatApi.getMessages(room.id, { page: 0, size: 50 }, tokens.access);
            if (data?.content) {
                setMessages([...data.content].reverse()); // oldest first
                scrollToBottom();
            }
        } catch (err) {
            console.error('Failed to load messages:', err);
        }
    }, [tokens, scrollToBottom]);

    const loadRooms = useCallback(async () => {
        if (!tokens?.access) return;
        try {
            const data = await chatApi.getRooms(tokens.access);
            if (Array.isArray(data)) { setRooms(data); setRoomsLoaded(true); }
        } catch (err) {
            console.error('Failed to load rooms:', err);
        }
    }, [tokens]);

    // ── Initial unread badge ──────────────────────────────────────────
    useEffect(() => {
        if (isAuthenticated && tokens?.access) {
            chatApi.getUnreadCount(tokens.access).then(setUnreadCount).catch(() => { });
        }
    }, [isAuthenticated, tokens]);

    // ── Fetch rooms when widget opens (and no room is active) ─────────
    useEffect(() => {
        if (isOpen && !activeRoom) {
            loadRooms();
        }
    }, [isOpen, activeRoom, loadRooms]);

    // ── Message polling for active room ───────────────────────────────
    useEffect(() => {
        stopPolling();
        if (!activeRoom?.id || !isOpen || !tokens?.access) return;

        loadMessages(activeRoom);

        pollingRef.current = setInterval(() => {
            chatApi.getMessages(activeRoom.id, { page: 0, size: 50 }, tokens.access)
                .then(data => { if (data?.content) setMessages([...data.content].reverse()); })
                .catch(() => { });
        }, 4000);

        return stopPolling;
    }, [activeRoom, isOpen, tokens, loadMessages, stopPolling]);

    // ── Auto-focus input when entering a room ─────────────────────────
    useEffect(() => {
        if (activeRoom && inputRef.current) {
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [activeRoom]);

    // ── Global openChat event ─────────────────────────────────────────
    useEffect(() => {
        const handleOpenChat = async (e) => {
            if (!isAuthenticated || !tokens?.access) return;
            const { receiverUserId, listingId, receiverName, receiverPic } = e.detail || {};

            setIsOpen(true);
            setLoadingRoom(true);
            setActiveRoom(null); // clear previous room while loading
            setMessages([]);

            try {
                // Build room request — receiverUserId may be absent from some callers
                const roomReq = {
                    listingId: String(listingId),
                    ...(receiverUserId ? { receiverUserId: String(receiverUserId) } : {}),
                };
                const res = await chatApi.createOrGetRoom(roomReq, tokens.access);
                if (res) {
                    // Backfill metadata if API omitted it
                    const room = {
                        ...res,
                        otherUserName: res.otherUserName || receiverName || 'User',
                        otherUserProfilePic: res.otherUserProfilePic || receiverPic || null,
                    };
                    setActiveRoom(room);
                    // Refresh room list so it stays current
                    loadRooms();
                }
            } catch (err) {
                console.error('Failed to open/create chat room:', err);
                if (err.paymentRequired) {
                    // Starting a new conversation is a Prime feature — sell it
                    // instead of dumping the user on an empty room list.
                    setIsOpen(false);
                    openPrimeModal(err.message || 'Chat with owners and flatmates without limits.');
                    setLoadingRoom(false);
                    return;
                }
                // Fall back to room list view
                setActiveRoom(null);
                loadRooms();
            } finally {
                setLoadingRoom(false);
            }
        };

        window.addEventListener('openChat', handleOpenChat);
        return () => window.removeEventListener('openChat', handleOpenChat);
    }, [isAuthenticated, tokens, loadRooms]);

    // ── Send message ──────────────────────────────────────────────────
    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() || !activeRoom?.id || sendingMessage) return;

        const content = newMessage.trim();
        setNewMessage('');
        setSendingMessage(true);

        // Optimistic update
        const optimisticMsg = {
            id: `temp-${Date.now()}`,
            senderId: user?.id || '__me__',
            content,
            sentAt: new Date().toISOString(),
            _optimistic: true,
        };
        setMessages(prev => [...prev, optimisticMsg]);
        scrollToBottom();

        try {
            const result = await chatApi.sendMessage({ chatRoomId: activeRoom.id, content }, tokens.access);
            if (result) {
                // Replace optimistic with real message
                setMessages(prev => prev.map(m => m._optimistic ? result : m));
            }
        } catch (err) {
            console.error('Send failed:', err);
            // Remove optimistic msg and restore input
            setMessages(prev => prev.filter(m => !m._optimistic));
            setNewMessage(content);
            if (err.paymentRequired) {
                // Also reached when a membership lapses mid-conversation.
                openPrimeModal(err.message || 'Subscribe to keep chatting.');
            }
        } finally {
            setSendingMessage(false);
            scrollToBottom();
        }
    };

    // ── Message ownership ─────────────────────────────────────────────
    const isMine = (msg) => {
        if (msg._optimistic) return true;
        if (!activeRoom?.otherUserId) return false;
        return msg.senderId !== activeRoom.otherUserId;
    };

    // ── Select room from list ─────────────────────────────────────────
    const handleSelectRoom = (room) => {
        setMessages([]);
        setActiveRoom(room);
    };

    if (!isAuthenticated) return null;

    return (
        <div style={{ position: 'fixed', bottom: isMobile ? '0' : '24px', right: isMobile ? '0' : '24px', zIndex: 9999, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', ...(isMobile && isOpen ? { inset: 0 } : {}) }}>

            {/* ── CHAT WINDOW ── */}
            {isOpen && (
                <div style={{ width: isMobile ? '100%' : '380px', height: isMobile ? '100%' : '560px', backgroundColor: 'white', borderRadius: isMobile ? '0' : '24px', boxShadow: isMobile ? 'none' : '0 20px 60px rgba(0,0,0,0.18)', marginBottom: isMobile ? '0' : '16px', display: 'flex', flexDirection: 'column', overflow: 'hidden', border: isMobile ? 'none' : '1px solid #E4EFC0' }}>

                    {/* Header */}
                    <div style={{ background: `linear-gradient(135deg, ${BLUE_DARK}, ${BLUE})`, color: 'white', padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                        {activeRoom ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
                                <button onClick={() => { setActiveRoom(null); setMessages([]); }} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', cursor: 'pointer', padding: '6px', borderRadius: '10px', display: 'flex', flexShrink: 0 }}>
                                    <IconArrowLeft size={20} />
                                </button>
                                <Avatar src={activeRoom.otherUserProfilePic} name={activeRoom.otherUserName} size={36} />
                                <div style={{ minWidth: 0 }}>
                                    <div style={{ fontWeight: '800', fontSize: '15px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{activeRoom.otherUserName || 'Chat'}</div>
                                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.7)' }}>FlatLoop Chat</div>
                                </div>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <IconChat size={20} />
                                <span style={{ fontWeight: '800', fontSize: '17px' }}>Your Chats</span>
                            </div>
                        )}
                        <button onClick={() => setIsOpen(false)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', cursor: 'pointer', padding: '6px', borderRadius: '10px', display: 'flex', flexShrink: 0 }}>
                            <IconClose size={20} />
                        </button>
                    </div>

                    {/* Loading room spinner */}
                    {loadingRoom && (
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px', color: '#6B7280' }}>
                            <div style={{ width: '40px', height: '40px', border: `3px solid #E4EFC0`, borderTopColor: BLUE, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                            <span style={{ fontSize: '14px', fontWeight: '600' }}>Opening chat…</span>
                        </div>
                    )}

                    {/* Room List */}
                    {!loadingRoom && !activeRoom && (
                        <div style={{ flex: 1, overflowY: 'auto', background: '#F3F4F6' }}>
                            {rooms.length === 0 ? (
                                <div style={{ padding: '48px 24px', textAlign: 'center', color: '#9CA3AF' }}>
                                    <IconChat size={48} color="#D1D5DB" />
                                    <p style={{ marginTop: '16px', fontSize: '14px', fontWeight: '600' }}>No active conversations yet.</p>
                                    <p style={{ fontSize: '12px', color: '#C0C0C0', marginTop: '4px' }}>Chat with owners from their listing page.</p>
                                </div>
                            ) : (
                                rooms.map(room => (
                                    <div key={room.id} onClick={() => handleSelectRoom(room)}
                                        style={{ padding: '14px 18px', display: 'flex', gap: '12px', cursor: 'pointer', borderBottom: '1px solid #E5E9E3', background: 'white', transition: 'background 0.15s', alignItems: 'center' }}
                                        onMouseEnter={e => e.currentTarget.style.background = '#EFF6FF'}
                                        onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                                        <div style={{ position: 'relative', flexShrink: 0 }}>
                                            <Avatar src={room.otherUserProfilePic} name={room.otherUserName} size={46} />
                                            {room.unreadCount > 0 && (
                                                <span style={{ position: 'absolute', top: -2, right: -2, background: '#EF4444', color: 'white', fontSize: '10px', fontWeight: '800', width: '18px', height: '18px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid white' }}>
                                                    {room.unreadCount > 9 ? '9+' : room.unreadCount}
                                                </span>
                                            )}
                                        </div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '3px' }}>
                                                <span style={{ fontWeight: '700', color: '#111827', fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '160px' }}>{room.otherUserName || 'User'}</span>
                                                <span style={{ fontSize: '11px', color: '#9CA3AF', flexShrink: 0, marginLeft: '8px' }}>
                                                    {room.lastMessageAt ? new Date(room.lastMessageAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : ''}
                                                </span>
                                            </div>
                                            <div style={{ color: room.unreadCount > 0 ? '#1E40AF' : '#6B7280', fontSize: '13px', fontWeight: room.unreadCount > 0 ? '600' : '400', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {room.lastMessage || 'Start a conversation'}
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    )}

                    {/* Chat Messages View */}
                    {!loadingRoom && activeRoom && (
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                            {/* Messages */}
                            <div style={{ flex: 1, padding: '16px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '10px', background: '#F3F4F6' }}>
                                {messages.length === 0 ? (
                                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#9CA3AF', gap: '8px' }}>
                                        <IconChat size={32} color="#D1D5DB" />
                                        <span style={{ fontSize: '14px', fontWeight: '600' }}>Say hi to {activeRoom.otherUserName || 'the owner'}!</span>
                                    </div>
                                ) : (
                                    messages.map((msg, idx) => {
                                        const mine = isMine(msg);
                                        return (
                                            <div key={msg.id || idx} style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start' }}>
                                                <div style={{
                                                    background: mine ? `linear-gradient(135deg, ${BLUE_DARK}, ${BLUE})` : 'white',
                                                    color: mine ? 'white' : '#1F2937',
                                                    padding: '10px 14px',
                                                    borderRadius: '18px',
                                                    borderBottomRightRadius: mine ? '4px' : '18px',
                                                    borderBottomLeftRadius: mine ? '18px' : '4px',
                                                    maxWidth: '80%',
                                                    boxShadow: mine ? '0 4px 12px rgba(34,106,70,0.25)' : '0 2px 8px rgba(0,0,0,0.06)',
                                                    border: mine ? 'none' : '1px solid #DDE3DC',
                                                    opacity: msg._optimistic ? 0.7 : 1,
                                                }}>
                                                    <div style={{ fontSize: '14px', lineHeight: '1.5', wordBreak: 'break-word' }}>{msg.content}</div>
                                                    <div style={{ fontSize: '10px', opacity: 0.7, marginTop: '4px', textAlign: 'right' }}>
                                                        {msg.sentAt ? new Date(msg.sentAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ''}
                                                        {msg._optimistic && ' ✓'}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                                <div ref={messagesEndRef} />
                            </div>

                            {/* Input */}
                            <form onSubmit={handleSendMessage} style={{ padding: '12px 14px', borderTop: '1px solid #DDE3DC', background: 'white', display: 'flex', gap: '10px', alignItems: 'center', flexShrink: 0 }}>
                                <input
                                    ref={inputRef}
                                    type="text"
                                    value={newMessage}
                                    onChange={e => setNewMessage(e.target.value)}
                                    placeholder="Type a message…"
                                    style={{ flex: 1, background: '#F1F5FF', border: '1px solid #E4EFC0', borderRadius: '24px', padding: '11px 18px', fontSize: '14px', outline: 'none', fontFamily: 'inherit', color: '#111827', transition: 'border 0.2s' }}
                                    onFocus={e => e.target.style.borderColor = BLUE}
                                    onBlur={e => e.target.style.borderColor = '#E4EFC0'}
                                />
                                <button
                                    type="submit"
                                    disabled={!newMessage.trim() || sendingMessage}
                                    style={{ background: newMessage.trim() && !sendingMessage ? `linear-gradient(135deg, ${BLUE_DARK}, ${BLUE})` : '#DDE3DC', color: 'white', border: 'none', width: '42px', height: '42px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: newMessage.trim() && !sendingMessage ? 'pointer' : 'default', transition: 'all 0.2s', flexShrink: 0, boxShadow: newMessage.trim() ? '0 4px 12px rgba(34,106,70,0.3)' : 'none' }}>
                                    <IconSend size={16} />
                                </button>
                            </form>
                        </div>
                    )}
                </div>
            )}

            {/* ── FAB ── */}
            {!(isMobile && isOpen) && <button
                onClick={() => { setIsOpen(prev => !prev); if (!isOpen) { setActiveRoom(null); setMessages([]); } }}
                style={{ backgroundColor: BLUE, color: 'white', width: '60px', height: '60px', border: 'none', borderRadius: '50%', boxShadow: `0 8px 32px rgba(34,106,70,0.4)`, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', transition: 'transform 0.2s', transform: isOpen ? 'scale(0.9)' : 'scale(1)' }}>
                {isOpen ? <IconClose size={26} /> : <IconChat size={26} />}
                {!isOpen && unreadCount > 0 && (
                    <span style={{ position: 'absolute', top: 0, right: 0, background: '#EF4444', color: 'white', fontSize: '11px', fontWeight: '800', width: '20px', height: '20px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid white' }}>
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>}

            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
};

export default ChatWidget;
